// =====================================================
// Swiss Finance OS - Receipt Service
// Service for handling receipt uploads and storage
// =====================================================

import { supabase, getCurrentUserOrganization } from '@/integrations/supabase/client';

interface UploadReceiptParams {
  transactionId: string;
  mandateId: string;
  file: File;
}

interface ReceiptUpload {
  id: string;
  transaction_id: string;
  organization_id: string;
  mandate_id: string;
  storage_path: string;
  file_name: string;
  file_size: number;
  mime_type: string;
  uploaded_by: string;
  uploaded_at: string;
  ocr_status: 'pending' | 'processing' | 'processed' | 'failed';
  ocr_processed_at?: string;
  extracted_data?: Record<string, any>;
  ocr_confidence?: number;
  ocr_error?: string;
  validation_results?: Record<string, any>;
  has_mismatches?: boolean;
  metadata?: Record<string, any>;
  created_at: string;
  updated_at: string;
}

interface UploadResult {
  success: boolean;
  receiptId?: string;
  publicUrl?: string;
  error?: string;
}

interface DeleteResult {
  success: boolean;
  error?: string;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/heic'
];

const ALLOWED_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png', 'heic'];

class ReceiptService {
  /**
   * Validate file before upload
   */
  validateFile(file: File): { valid: boolean; error?: string } {
    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return {
        valid: false,
        error: `File size exceeds 10MB limit (${(file.size / 1024 / 1024).toFixed(2)}MB)`
      };
    }

    // Check mime type
    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
      return {
        valid: false,
        error: `Invalid file type: ${file.type}. Allowed types: PDF, JPG, PNG, HEIC`
      };
    }

    // Check file extension
    const extension = file.name.split('.').pop()?.toLowerCase();
    if (!extension || !ALLOWED_EXTENSIONS.includes(extension)) {
      return {
        valid: false,
        error: `Invalid file extension: .${extension}. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`
      };
    }

    return { valid: true };
  }

  /**
   * Upload receipt for a transaction
   */
  async uploadReceipt({
    transactionId,
    mandateId,
    file
  }: UploadReceiptParams): Promise<UploadResult> {
    try {
      // Validate file
      const validation = this.validateFile(file);
      if (!validation.valid) {
        return {
          success: false,
          error: validation.error
        };
      }

      // Get current user and organization
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return {
          success: false,
          error: 'User not authenticated'
        };
      }

      const userOrg = await getCurrentUserOrganization();
      if (!userOrg || !userOrg.company_id) {
        return {
          success: false,
          error: 'Organization not found'
        };
      }

      const organizationId = userOrg.company_id;

      // Generate unique filename
      const timestamp = Date.now();
      const fileExt = file.name.split('.').pop()?.toLowerCase();
      const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const uniqueFileName = `${timestamp}_${sanitizedFileName}`;

      // Storage path: {org_id}/{mandate_id}/{transaction_id}/{filename}
      const storagePath = `${organizationId}/${mandateId}/${transactionId}/${uniqueFileName}`;

      // Upload to Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('transaction-receipts')
        .upload(storagePath, file, {
          cacheControl: '3600',
          upsert: false // Don't overwrite - each upload gets unique name
        });

      if (uploadError) {
        console.error('Storage upload error:', uploadError);
        return {
          success: false,
          error: `Upload failed: ${uploadError.message}`
        };
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('transaction-receipts')
        .getPublicUrl(storagePath);

      // Create database record
      const { data: receiptData, error: dbError } = await (supabase as any)
        .from('receipt_uploads')
        .insert({
          transaction_id: transactionId,
          organization_id: organizationId,
          mandate_id: mandateId,
          storage_path: storagePath,
          file_name: file.name,
          file_size: file.size,
          mime_type: file.type,
          uploaded_by: user.id,
          ocr_status: 'pending',
          metadata: {
            original_filename: file.name,
            upload_timestamp: new Date().toISOString()
          }
        })
        .select()
        .single();

      if (dbError) {
        console.error('Database insert error:', dbError);
        // Try to cleanup storage
        await supabase.storage
          .from('transaction-receipts')
          .remove([storagePath]);

        return {
          success: false,
          error: `Failed to save receipt record: ${dbError.message}`
        };
      }

      return {
        success: true,
        receiptId: receiptData.id,
        publicUrl: urlData.publicUrl
      };
    } catch (error) {
      console.error('Receipt upload error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown upload error'
      };
    }
  }

  /**
   * Get receipts for a transaction
   */
  async getTransactionReceipts(transactionId: string): Promise<ReceiptUpload[]> {
    const userOrg = await getCurrentUserOrganization();
    if (!userOrg || !userOrg.company_id) {
      throw new Error('Organization not found');
    }

    const { data, error } = await (supabase as any)
      .from('receipt_uploads')
      .select('*')
      .eq('transaction_id', transactionId)
      .eq('organization_id', userOrg.company_id)
      .order('uploaded_at', { ascending: false });

    if (error) {
      console.error('Error fetching receipts:', error);
      throw new Error(`Failed to fetch receipts: ${error.message}`);
    }

    return (data || []) as ReceiptUpload[];
  }

  /**
   * Get receipt download URL with signed URL for security
   */
  async getReceiptUrl(storagePath: string): Promise<string | null> {
    try {
      const { data, error } = await supabase.storage
        .from('transaction-receipts')
        .createSignedUrl(storagePath, 3600); // 1 hour expiry

      if (error) {
        console.error('Error creating signed URL:', error);
        return null;
      }

      return data.signedUrl;
    } catch (error) {
      console.error('Error getting receipt URL:', error);
      return null;
    }
  }

  /**
   * Delete receipt
   */
  async deleteReceipt(receiptId: string): Promise<DeleteResult> {
    try {
      const userOrg = await getCurrentUserOrganization();
      if (!userOrg || !userOrg.company_id) {
        return {
          success: false,
          error: 'Organization not found'
        };
      }

      // Get receipt record to find storage path
      const { data: receipt, error: fetchError } = await (supabase as any)
        .from('receipt_uploads')
        .select('storage_path, organization_id')
        .eq('id', receiptId)
        .eq('organization_id', userOrg.company_id)
        .single();

      if (fetchError || !receipt) {
        return {
          success: false,
          error: 'Receipt not found or access denied'
        };
      }

      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('transaction-receipts')
        .remove([receipt.storage_path]);

      if (storageError) {
        console.warn('Storage delete error:', storageError);
        // Continue even if storage delete fails
      }

      // Delete database record
      const { error: dbError } = await (supabase as any)
        .from('receipt_uploads')
        .delete()
        .eq('id', receiptId)
        .eq('organization_id', userOrg.company_id);

      if (dbError) {
        return {
          success: false,
          error: `Failed to delete receipt record: ${dbError.message}`
        };
      }

      return { success: true };
    } catch (error) {
      console.error('Receipt delete error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown delete error'
      };
    }
  }

  /**
   * Update OCR status (for Agent 4 to use)
   */
  async updateOcrStatus(
    receiptId: string,
    status: 'processing' | 'processed' | 'failed',
    data?: {
      extractedData?: Record<string, any>;
      confidence?: number;
      error?: string;
      validationResults?: Record<string, any>;
      hasMismatches?: boolean;
    }
  ): Promise<void> {
    const updates: any = {
      ocr_status: status,
      ocr_processed_at: new Date().toISOString()
    };

    if (data?.extractedData) {
      updates.extracted_data = data.extractedData;
    }

    if (data?.confidence !== undefined) {
      updates.ocr_confidence = data.confidence;
    }

    if (data?.error) {
      updates.ocr_error = data.error;
    }

    if (data?.validationResults) {
      updates.validation_results = data.validationResults;
    }

    if (data?.hasMismatches !== undefined) {
      updates.has_mismatches = data.hasMismatches;
    }

    const { error } = await (supabase as any)
      .from('receipt_uploads')
      .update(updates)
      .eq('id', receiptId);

    if (error) {
      console.error('Error updating OCR status:', error);
      throw new Error(`Failed to update OCR status: ${error.message}`);
    }
  }

  /**
   * Get receipts pending OCR processing
   */
  async getPendingOcrReceipts(limit: number = 10): Promise<ReceiptUpload[]> {
    const userOrg = await getCurrentUserOrganization();
    if (!userOrg || !userOrg.company_id) {
      throw new Error('Organization not found');
    }

    const { data, error } = await (supabase as any)
      .from('receipt_uploads')
      .select('*')
      .eq('organization_id', userOrg.company_id)
      .eq('ocr_status', 'pending')
      .order('uploaded_at', { ascending: true })
      .limit(limit);

    if (error) {
      console.error('Error fetching pending receipts:', error);
      throw new Error(`Failed to fetch pending receipts: ${error.message}`);
    }

    return (data || []) as ReceiptUpload[];
  }
}

export const receiptService = new ReceiptService();
export type { ReceiptUpload, UploadResult, DeleteResult, UploadReceiptParams };
